import React, { useContext, useEffect, useState } from 'react'
import * as S from './styles'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search2.svg'
import { useHistory, useLocation } from 'react-router'
import AmbulatoryCard from 'presentation/doctor/components/AmbulatoryCard'
import {
  AmbulatoryAuthorizationStatus,
  AmbulatoryAuthorizationStatusPlural
} from 'common/enum/ambulatory-authorization-status'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import TabsNew from 'presentation/shared/components/TabsNew'
import { AmbulatoryAuthorizationDashboardContext } from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import { GetAmbulatoryOrders } from 'domain/usecases/ambulatory/get-ambulatory-orders'
import { AmbulatoryOrderModel } from 'domain/entities/ambulatory-orders-model'
import { SearchAmbulatoryOrders } from 'domain/usecases/ambulatory/search-ambulatory-order'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'

type Props = {
  getAmbulatoryOrders: (params: GetAmbulatoryOrders.Params) => void
  searchAmbulatoryOrders: (params: SearchAmbulatoryOrders.Params) => void
}

export default function AmbulatoryOrderStatusListLayout({
  getAmbulatoryOrders,
  searchAmbulatoryOrders
}: Props) {
  const history = useHistory()
  const location = useLocation<any>()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentTab, setCurrentTab] = useState(0)

  const onEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      getAmbulatoryOrderFromSearch()
    }
  }

  const locationState = location.state

  const getAmbulatoryOrderFromSearch = () => {
    if (!searchTerm) {
      return
    }
    searchAmbulatoryOrders({
      search: searchTerm
    })
  }

  useEffect(() => {
    history.replace({ ...location, state: {} })
  }, [])

  function getCurrentTab() {
    if (locationState?.orderStatus) {
      const statusValue =
        AmbulatoryAuthorizationStatusPlural[
          locationState?.orderStatus as keyof typeof AmbulatoryAuthorizationStatusPlural
        ]

      const tabIndex = Tabs.findIndex((tab) => tab.label === statusValue)
      setCurrentTab(tabIndex)
    }
  }

  useEffect(() => {
    getCurrentTab()
  }, [])

  const Tabs = [
    {
      label: 'Em análise no hospital',
      componentChildren: (
        <GenericTab
          getAmbulatoryOrders={getAmbulatoryOrders}
          currentTab={currentTab}
          selectedStatus={locationState?.orderStatus}
          status={
            locationState?.orderStatus
              ? locationState?.orderStatus
              : AmbulatoryAuthorizationStatus.HOSPITAL_ANALYSIS
          }
        />
      )
    },
    {
      label: 'Em análise no convênio',
      componentChildren: (
        <GenericTab
          getAmbulatoryOrders={getAmbulatoryOrders}
          currentTab={currentTab}
          selectedStatus={locationState?.orderStatus}
          status={
            locationState?.orderStatus
              ? locationState?.orderStatus
              : AmbulatoryAuthorizationStatus.ANALYSIS_INSURANCE
          }
        />
      )
    },
    {
      label: 'Autorizados',
      componentChildren: (
        <GenericTab
          getAmbulatoryOrders={getAmbulatoryOrders}
          currentTab={currentTab}
          selectedStatus={locationState?.orderStatus}
          status={
            locationState?.orderStatus
              ? locationState?.orderStatus
              : AmbulatoryAuthorizationStatus.AUTHORIZED
          }
        />
      )
    },
    {
      label: 'Realizados',
      componentChildren: (
        <GenericTab
          getAmbulatoryOrders={getAmbulatoryOrders}
          currentTab={currentTab}
          selectedStatus={locationState?.orderStatus}
          status={
            locationState?.orderStatus
              ? locationState?.orderStatus
              : AmbulatoryAuthorizationStatus.DONE
          }
        />
      )
    },
    {
      label: 'Negados',
      componentChildren: (
        <GenericTab
          getAmbulatoryOrders={getAmbulatoryOrders}
          currentTab={currentTab}
          selectedStatus={locationState?.orderStatus}
          status={
            locationState?.orderStatus
              ? locationState?.orderStatus
              : AmbulatoryAuthorizationStatus.DENIED
          }
        />
      )
    },
    {
      label: 'Cancelados',
      componentChildren: (
        <GenericTab
          getAmbulatoryOrders={getAmbulatoryOrders}
          currentTab={currentTab}
          selectedStatus={locationState?.orderStatus}
          status={
            locationState?.orderStatus
              ? locationState?.orderStatus
              : AmbulatoryAuthorizationStatus.CANCELED
          }
        />
      )
    }
  ]

  return (
    <>
      <HeaderNew
        actualPageTitle="Pedidos ambulatoriais"
        actualPageOnClick={() =>
          history.push('/pedidos-autorizacao-ambulatorial/dashboard')
        }
      />

      <ContainerNew
        title="Pedidos ambulatoriais"
        titleColor="neutral800"
        titleSize="large"
        titleWeight="bold"
      >
        <TextFieldNew
          icon={<SearchIcon />}
          iconIsButton
          placeholder="Paciente, N° do aviso"
          value={searchTerm}
          onInputChange={setSearchTerm}
          onIconClick={() => getAmbulatoryOrderFromSearch()}
          onKeyDown={onEnterSearch}
        />

        <S.PendenciesContainer>
          <S.TabsWrapper>
            <TabsNew
              tabItems={Tabs}
              returnTab={currentTab}
              variant="scrollable"
              onChangeTab={(index) => setCurrentTab(index)}
            />
          </S.TabsWrapper>
        </S.PendenciesContainer>
      </ContainerNew>
    </>
  )
}

type GenericTabProps = {
  status: AmbulatoryAuthorizationStatus
  getAmbulatoryOrders: (params: GetAmbulatoryOrders.Params) => void
  currentTab: number
  selectedStatus?: AmbulatoryAuthorizationStatus
} & WithLoadingProps

const GenericTab = WithLoading(
  ({
    status,
    currentTab,
    selectedStatus,
    getAmbulatoryOrders
  }: GenericTabProps) => {
    const history = useHistory()

    useEffect(() => {
      if (selectedStatus) {
        getAmbulatoryOrders({
          status: selectedStatus
        })
      } else {
        getAmbulatoryOrders({
          status
        })
      }
    }, [currentTab])

    const { state } = useContext<any>(AmbulatoryAuthorizationDashboardContext)

    const handleRedirectToAmbulatoryOrderDetailsPage = (id: string) => {
      history.push('/pedidos-autorizacao-ambulatorial/detalhes', {
        orderId: id
      })
    }

    return (
      <>
        <S.CardWrapper>
          {state.ambulatoryOrdersList.map((order: AmbulatoryOrderModel) => {
            return (
              <AmbulatoryCard
                key={order.id}
                order={order}
                onClick={() =>
                  handleRedirectToAmbulatoryOrderDetailsPage(order.id)
                }
              />
            )
          })}
        </S.CardWrapper>
      </>
    )
  }
)
